/**
 * Inventario acumulativo con costo promedio ponderado (Req. 7, 8).
 *
 * CODIGO PURO: recibe un estado y devuelve un estado nuevo. No toca la base de
 * datos. Aqui viven los calculos que sostienen los costos de todo el negocio,
 * asi que estan aislados y con pruebas.
 *
 * LA DECISION CENTRAL: se llevan DOS cantidades por insumo.
 *
 *   stockBase         cantidad fisica total     -> responde "¿me alcanza?"
 *   stockValuadoBase  porcion con costo conocido -> responde "¿cuanto cuesta?"
 *
 * Motivo: la usuaria quiere registrar lo que ya tiene sin inventar precios. Si
 * esas existencias entraran con valor cero, abaratarian el promedio y las
 * ganancias saldrian infladas, que es justo lo que pidio evitar. Manteniendolas
 * fuera del acervo valuado, el promedio se calcula solo con compras reales
 * (Req. 8.5).
 *
 * El inventario NUNCA se reinicia por semana ni por mes (Req. 7.1).
 */

import type { Centavos } from './money';

export interface EstadoInventario {
  /** Cantidad fisica total, en unidad base. */
  stockBase: number;
  /** Porcion de la cantidad fisica que tiene costo conocido. */
  stockValuadoBase: number;
  /** Valor monetario de la porcion valuada. */
  valorCentavos: Centavos;
  /** Costo por unidad base de la ultima compra. Respaldo cuando no hay acervo valuado. */
  ultimoCostoPorBase: number | null;
}

export const INVENTARIO_VACIO: Readonly<EstadoInventario> = Object.freeze({
  stockBase: 0,
  stockValuadoBase: 0,
  valorCentavos: 0,
  ultimoCostoPorBase: null,
});

/** De donde salio el costo unitario, para poder advertirlo en pantalla. */
export type OrigenCosto = 'promedio' | 'ultima_compra' | 'desconocido';

export interface CostoUnitario {
  /** Centavos por unidad base. null si no hay ningun dato del cual partir. */
  costoPorBase: number | null;
  origen: OrigenCosto;
}

/** Deltas que se anotan en el libro de movimientos. */
export interface DeltasMovimiento {
  /** Cambio en la cantidad fisica, con signo. */
  cantidadBase: number;
  /** Cambio en la cantidad valuada, con signo. Puede diferir de la fisica. */
  cantidadValuadaBase: number;
  /** Cambio en el valor del acervo valuado, con signo. */
  valorCentavos: Centavos;
  /** false = la cantidad no toca el acervo valuado (Req. 8.4). */
  afectaValuacion: boolean;
  /** Costo economico reconocido para esta cantidad. null = no se pudo determinar. */
  costoAplicadoCentavos: Centavos | null;
  /** true cuando parte del costo se imputo desde el promedio conocido. */
  costoImputado: boolean;
}

export interface ResultadoInventario {
  estado: EstadoInventario;
  deltas: DeltasMovimiento;
  /**
   * Residuo que quedo al agotarse el acervo valuado y hubo que sanear a cero.
   * null cuando no hizo falta (Req. 7.7).
   */
  ajusteResidualCentavos: Centavos | null;
  /** Advertencias para mostrar en pantalla sin bloquear la operacion. */
  advertencias: string[];
}

// ---------------------------------------------------------------------------
// Costo unitario vigente
// ---------------------------------------------------------------------------

/**
 * Costo por unidad base vigente.
 *
 * El divisor es `stockValuadoBase` y NO `stockBase`. Ahi esta la garantia de que
 * las existencias sin costo no diluyan el promedio (Req. 8.5).
 */
export function costoPorBase(estado: EstadoInventario): CostoUnitario {
  if (estado.stockValuadoBase > 0) {
    return {
      costoPorBase: estado.valorCentavos / estado.stockValuadoBase,
      origen: 'promedio',
    };
  }

  if (estado.ultimoCostoPorBase !== null) {
    // Sin acervo valuado se usa el ultimo dato real conocido (Req. 10.6).
    return { costoPorBase: estado.ultimoCostoPorBase, origen: 'ultima_compra' };
  }

  return { costoPorBase: null, origen: 'desconocido' };
}

/** Cantidad fisica que no tiene costo conocido (Req. 8.6). */
export function cantidadSinCosto(estado: EstadoInventario): number {
  return Math.max(0, estado.stockBase - estado.stockValuadoBase);
}

// ---------------------------------------------------------------------------
// Entradas
// ---------------------------------------------------------------------------

/**
 * Entrada CON costo conocido: una compra, o un saldo inicial con precio.
 *
 * Verificacion del caso del planteamiento:
 *   12 kg por $1,499.00 -> 12000 g y 149900 centavos
 *   costo = 149900 / 12000 = 12.4917 centavos/g = $124.92 por kilogramo
 */
export function aplicarEntradaValuada(
  estado: EstadoInventario,
  cantidadBase: number,
  costoCentavos: Centavos,
): ResultadoInventario {
  const advertencias: string[] = [];

  if (cantidadBase <= 0) {
    advertencias.push('La cantidad de una entrada deberia ser mayor que cero.');
  }

  const unitario = cantidadBase > 0 ? costoCentavos / cantidadBase : estado.ultimoCostoPorBase;

  return {
    estado: {
      stockBase: estado.stockBase + cantidadBase,
      stockValuadoBase: estado.stockValuadoBase + cantidadBase,
      valorCentavos: estado.valorCentavos + costoCentavos,
      ultimoCostoPorBase: unitario,
    },
    deltas: {
      cantidadBase,
      cantidadValuadaBase: cantidadBase,
      valorCentavos: costoCentavos,
      afectaValuacion: true,
      costoAplicadoCentavos: costoCentavos,
      costoImputado: false,
    },
    ajusteResidualCentavos: null,
    advertencias,
  };
}

/**
 * Entrada SIN costo conocido: el saldo inicial de lo que ya se tenia (Req. 8.4).
 *
 * Solo aumenta la cantidad fisica. El acervo valuado y su valor quedan intactos,
 * asi que el promedio ponderado no se mueve.
 */
export function aplicarEntradaSinCosto(
  estado: EstadoInventario,
  cantidadBase: number,
): ResultadoInventario {
  const advertencias: string[] = [];

  if (cantidadBase <= 0) {
    advertencias.push('La cantidad de una entrada deberia ser mayor que cero.');
  }

  return {
    estado: {
      ...estado,
      stockBase: estado.stockBase + cantidadBase,
    },
    deltas: {
      cantidadBase,
      cantidadValuadaBase: 0,
      valorCentavos: 0,
      afectaValuacion: false,
      costoAplicadoCentavos: null,
      costoImputado: false,
    },
    ajusteResidualCentavos: null,
    advertencias,
  };
}

// ---------------------------------------------------------------------------
// Salidas
// ---------------------------------------------------------------------------

/**
 * Salida por consumo de produccion, merma o ajuste negativo.
 *
 * Se llevan dos numeros distintos a proposito, porque responden preguntas
 * distintas:
 *
 *   valorCentavos           contabilidad de acervo: cuadra contra el libro
 *   costoAplicadoCentavos   costo economico: alimenta el costeo de la produccion
 *
 * Cuando se consumen unidades sin costo propio, se les reconoce el promedio
 * conocido y el resultado se marca como imputado. Es la opcion menos
 * distorsiva: el costo viene de compras reales, no de un precio inventado, y la
 * interfaz siempre avisa cual numero lleva estimacion (Req. 8.7).
 *
 * La salida se PERMITE aunque no alcance el inventario, y se advierte
 * (Req. 13.9): bloquear dejaria a la usuaria atorada en la cocina por un
 * descuadre de captura.
 */
export function aplicarSalida(
  estado: EstadoInventario,
  cantidadBase: number,
): ResultadoInventario {
  const advertencias: string[] = [];

  if (cantidadBase <= 0) {
    advertencias.push('La cantidad de una salida deberia ser mayor que cero.');
  }

  if (cantidadBase > estado.stockBase) {
    advertencias.push(
      `El inventario quedara en negativo: se consumen ${cantidadBase} y hay ${estado.stockBase}.`,
    );
  }

  const { costoPorBase: unitario, origen } = costoPorBase(estado);

  // Cuanto de la salida sale del acervo valuado.
  const valuadoUsado = Math.min(Math.max(0, cantidadBase), Math.max(0, estado.stockValuadoBase));
  const costoImputado = cantidadBase > valuadoUsado && unitario !== null;

  const costoAplicado = unitario === null ? null : Math.round(cantidadBase * unitario);
  const valorSalida = unitario === null ? 0 : Math.round(valuadoUsado * unitario);

  if (origen === 'desconocido') {
    advertencias.push(
      'No hay ningun costo registrado para este insumo, asi que el costo de la salida queda incompleto.',
    );
  } else if (costoImputado) {
    advertencias.push(
      'Parte de la cantidad no tenia costo propio; se le aplico el costo promedio conocido.',
    );
  } else if (origen === 'ultima_compra') {
    advertencias.push('No hay existencias valuadas; se uso el costo de la ultima compra.');
  }

  let stockValuadoBase = estado.stockValuadoBase - valuadoUsado;
  let valorCentavos = estado.valorCentavos - valorSalida;
  let ajusteResidualCentavos: Centavos | null = null;

  // Al agotarse el acervo valuado no debe quedar valor huerfano (Req. 7.7).
  if (stockValuadoBase <= 0) {
    stockValuadoBase = 0;
    if (valorCentavos !== 0) {
      ajusteResidualCentavos = -valorCentavos;
      valorCentavos = 0;
    }
  }

  return {
    estado: {
      stockBase: estado.stockBase - cantidadBase,
      stockValuadoBase,
      valorCentavos,
      ultimoCostoPorBase: estado.ultimoCostoPorBase,
    },
    deltas: {
      cantidadBase: -cantidadBase,
      cantidadValuadaBase: -valuadoUsado,
      valorCentavos: -valorSalida,
      afectaValuacion: valuadoUsado > 0,
      costoAplicadoCentavos: costoAplicado,
      costoImputado,
    },
    ajusteResidualCentavos,
    advertencias,
  };
}

// ---------------------------------------------------------------------------
// Reconstruccion desde el libro
// ---------------------------------------------------------------------------

/** Lo minimo que hace falta de un movimiento para reconstruir el estado. */
export interface MovimientoParaRecalculo {
  cantidadBase: number;
  cantidadValuadaBase: number;
  valorCentavos: Centavos;
}

/**
 * Reconstruye las existencias y el valor sumando el libro de movimientos
 * (Req. 7.10, 18.3).
 *
 * Es una suma y no una repeticion de los calculos: cada movimiento ya guardo
 * sus tres deltas, asi que el resultado no depende de recalcular costos ni del
 * orden. Esto da un camino de reparacion ante cualquier descuadre.
 *
 * `ultimoCostoPorBase` no se reconstruye aqui porque no es un acumulado: lo
 * conserva el insumo.
 */
export function recalcularDesdeMovimientos(
  movimientos: readonly MovimientoParaRecalculo[],
  ultimoCostoPorBase: number | null = null,
): EstadoInventario {
  let stockBase = 0;
  let stockValuadoBase = 0;
  let valorCentavos = 0;

  for (const m of movimientos) {
    stockBase += m.cantidadBase;
    stockValuadoBase += m.cantidadValuadaBase;
    valorCentavos += m.valorCentavos;
  }

  return {
    stockBase: redondearCantidad(stockBase),
    stockValuadoBase: redondearCantidad(stockValuadoBase),
    valorCentavos: Math.round(valorCentavos),
    ultimoCostoPorBase,
  };
}

/** Recorta la cola del punto flotante que dejan las sumas encadenadas. */
function redondearCantidad(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

export interface DiferenciaInventario {
  campo: 'stockBase' | 'stockValuadoBase' | 'valorCentavos';
  guardado: number;
  recalculado: number;
  diferencia: number;
}

/**
 * Compara el estado guardado contra el recalculado desde el libro (Req. 18.4).
 * Devuelve las diferencias encontradas; vacio significa que cuadra.
 */
export function compararConLibro(
  guardado: EstadoInventario,
  recalculado: EstadoInventario,
): DiferenciaInventario[] {
  const campos: Array<DiferenciaInventario['campo']> = [
    'stockBase',
    'stockValuadoBase',
    'valorCentavos',
  ];

  return campos
    .map((campo) => ({
      campo,
      guardado: guardado[campo],
      recalculado: recalculado[campo],
      diferencia: redondearCantidad(guardado[campo] - recalculado[campo]),
    }))
    .filter((d) => d.diferencia !== 0);
}
